/**
 * Cross-offer card on the signed-in owner's dashboard.
 *
 * The list endpoint returns at most one card. This still shows only one, and
 * a church account never sees Operate even if a response includes it.
 * Copy speaks to the business owner. Hop opens a new tab the same way the
 * editor hop does: during the click, then replace that tab's location.
 * noopener drops the window handle, and opening '' after await stays blank.
 */

const PROMO_ORDER = ['operate', 'leads', 'laser'];
const CHURCH_SKIP = ['operate', 'leads'];
export const MAX_PROMO_CARDS = 2;

/** Succeed marketing packs. Linked only; another team owns the site. */
export const SUCCEED_URL = 'https://succeed.unitedundergod.org/';

export const PROMO_CARDS = {
  operate: {
    kicker: 'For your business',
    title: 'More profit. Less headache.',
    reason: 'Money, stock, orders and jobs on one desk. $49 a month, and your site details come with you.',
    action: 'See Operate',
  },
  leads: {
    kicker: 'Local Leads',
    title: 'Get local job requests sent to you.',
    reason: 'Neighbors ask for work near you. Free to join and free to accept. You only pay on jobs you win: 5%, $25 minimum. Your business name, phone and town come with you.',
    action: 'Join Local Leads',
  },
  laser: {
    kicker: 'For your business',
    title: 'Gifts and signs with your name on them.',
    reason: 'Engraved, branded pieces your customers keep.',
    action: 'See Laser Engrave Market',
  },
};

function line(value) {
  return String(value || '').trim().toLowerCase();
}

export function recordIsChurch(doc) {
  if (!doc || typeof doc !== 'object') return false;
  const audience = line(doc.audience);
  const businessType = line(doc.business_type);
  const source = line(doc.source);
  const layout = line(doc.starter_layout);
  if (audience === 'church' || businessType === 'church') return true;
  if (source === 'churchconnect') return true;
  if (String(doc.churchconnect_user_id || '').trim()) return true;
  if (layout === 'welcoming-church') return true;
  return false;
}

function siteIsBusiness(doc) {
  if (!doc || typeof doc !== 'object' || recordIsChurch(doc)) return false;
  const businessType = line(doc.business_type);
  return Boolean(businessType) && businessType !== 'church' && businessType !== 'personal';
}

/**
 * Church accounts never see Operate or Local Leads (they get ChurchConnect).
 * A ChurchConnect sign-in alone does not make a business site a church: an
 * account with a business site and no church site is a business.
 */
export function accountIsChurch(dashboard) {
  if (!dashboard || typeof dashboard !== 'object') return false;
  const sites = Array.isArray(dashboard.websites) ? dashboard.websites : [];
  if (sites.some(recordIsChurch)) return true;
  const client = dashboard.client && typeof dashboard.client === 'object' ? dashboard.client : {};
  if (line(client.audience) === 'church' || line(client.business_type) === 'church') return true;
  if (sites.some(siteIsBusiness)) return false;
  return recordIsChurch(client);
}

/** The cards to render (at most two), in server order. Churches skip Operate and Local Leads. */
export function visiblePromos(promos, dashboard) {
  const list = Array.isArray(promos) ? promos : [];
  const church = accountIsChurch(dashboard);
  const out = [];
  for (const item of list) {
    const key = line(item && item.key);
    if (!PROMO_ORDER.includes(key) || out.includes(key)) continue;
    if (CHURCH_SKIP.includes(key) && church) continue;
    out.push(key);
    if (out.length >= MAX_PROMO_CARDS) break;
  }
  return out;
}

/** The first card, or '' when there is nothing to show. */
export function visiblePromo(promos, dashboard) {
  return visiblePromos(promos, dashboard)[0] || '';
}

export function promoCard(key) {
  return PROMO_CARDS[key] || null;
}

export function safeHopUrl(value) {
  const text = String(value || '').trim();
  if (!text.startsWith('https://')) return '';
  let parsed;
  try {
    parsed = new URL(text);
  } catch (_) {
    return '';
  }
  if (parsed.protocol !== 'https:' || !parsed.hostname) return '';
  if (parsed.username || parsed.password) return '';
  return parsed.toString();
}

export function friendlyHopError(status) {
  const code = Number(status);
  if (code === 403) return "This offer isn't available on this account.";
  if (code === 409) return 'Add your name, the business name, and a phone number in your account, then try again.';
  if (code === 503) return "This isn't available right now. Try again in a little while.";
  return "We couldn't open that just now. Please try again.";
}

export function friendlyDismissError() {
  return "Couldn't hide this just now. Please try again.";
}

export function friendlyPopupError() {
  return 'Your browser blocked the new tab. Allow pop-ups for this site, then try again.';
}

function closeBlankTab(tab) {
  try {
    if (tab && typeof tab.close === 'function') tab.close();
  } catch (_) {
    /* a blocked or closed tab is not a crash */
  }
}

/**
 * Open the cross-offer in a new tab.
 *
 * Call this directly from the click so the blank tab opens before any await.
 * Then point that tab at the hop URL. A missing URL or a failed hop closes
 * the blank tab. A blocked popup leaves the dashboard where it is.
 */
export async function openPromoHop(requestUrl) {
  const tab = window.open('about:blank', '_blank');
  let raw = '';
  try {
    raw = await requestUrl();
  } catch (err) {
    closeBlankTab(tab);
    throw err;
  }
  const href = safeHopUrl(raw);
  if (!href) {
    closeBlankTab(tab);
    const error = new Error('no_url');
    error.code = 'no_url';
    throw error;
  }
  if (!tab) {
    const blocked = new Error('popup_blocked');
    blocked.code = 'popup_blocked';
    throw blocked;
  }
  tab.location.replace(href);
  if (typeof tab.focus === 'function') tab.focus();
}
